// FILE: PinnedUserMessage.tsx
// Purpose: Renders the user message pinned at the top of the transcript while its turn is being read.
// Layer: Web chat presentation component
// Exports: PinnedUserMessage

import { memo } from "react";
import { DEFAULT_CHAT_FONT_SIZE_PX } from "../../appSettings";
import { getChatTranscriptTextStyle } from "./chatTypography";
import {
  derivePinnedUserMessageText,
  type PinnedUserMessageContent,
} from "./PinnedUserMessage.logic";

interface PinnedUserMessageProps {
  message: PinnedUserMessageContent;
  chatFontSizePx?: number;
  onClick: () => void;
}

/**
 * The pinned row floats over the virtualized transcript, so it cannot inherit
 * the bubble's translucent `bg-secondary` alone: that token is a 4% alpha and
 * the rows scrolling underneath would show through the text. The outer element
 * lays down an opaque `bg-background` and the inner one composites the same
 * `bg-secondary` on top, which reproduces the bubble exactly as it looks
 * sitting on the page — in both themes, with no hardcoded colors.
 */
export const PinnedUserMessage = memo(function PinnedUserMessage(props: PinnedUserMessageProps) {
  const text = derivePinnedUserMessageText(props.message);
  if (text.length === 0) {
    return null;
  }

  return (
    <button
      type="button"
      data-pinned-user-message=""
      onClick={props.onClick}
      title={text}
      aria-label={`Jump to this message in the conversation: ${text}`}
      className="group block w-full cursor-pointer rounded-b-lg bg-background text-left shadow-[0_10px_18px_-8px_rgb(0_0_0/0.35)] dark:shadow-[0_14px_28px_-4px_rgb(0_0_0/0.95)]"
    >
      {/* `--secondary` and `--accent` are both a 4% alpha, so a `hover:bg-accent`
          would be invisible here. Composite an explicit foreground tint instead. */}
      <div className="rounded-b-lg border border-t-0 border-border/70 bg-secondary px-3 pt-[3px] pb-[5px] transition-colors group-hover:bg-foreground/8">
        <span
          className="line-clamp-2 wrap-break-word font-system-ui text-foreground"
          style={getChatTranscriptTextStyle(props.chatFontSizePx ?? DEFAULT_CHAT_FONT_SIZE_PX)}
        >
          {text}
        </span>
      </div>
    </button>
  );
});
